import { Doc } from '@dadohr/shared/domainTypes/document';
import { SwagUpIntegrationConfig } from '@dadohr/shared/domainTypes/integrations/swagUp';
import styled from '@emotion/styled';
import { Button, Dialog, DialogContent, Typography } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { Functionality } from '../..';
import { ButtonWithPromise } from '../../../../../../components/ButtonWithPromise';
import { DialogActionsWithSlots } from '../../../../../../components/DialogActionsWithSlots';
import { DialogTitleWithTypography } from '../../../../../../components/DialogTitleWithTypography';
import ENVS from '../../../../../../env.json';
import { useTranslation } from '../../../../../../i18next';
import { useCurrentUser } from '../../../../../../services/currentUser';
import { PageOAuthRedirectProps } from '../../../../../OAuth/pages/Redirect';
import {
  IntegrationConfigurationButton,
  IntegrationSetupDialogProperties,
  IntegrationSetupDialogWithConfigProperties
} from '../common';
import { OAuthSetupDialog } from '../oauth';
import { removeSwagUpConfig, useSwagUpConfig } from './service';

const NAME = 'SwagUp';

export const SWAG_UP_OAUTH_REDIRECT_PROPS: PageOAuthRedirectProps = {
  integrationName: NAME,
  cloudFn: 'swagUp_exchangeCodeAndStoreCredentials'
};

const Content = styled(DialogContent)((p) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: p.theme.spacing(2)
}));

const OAUTH = ENVS.swagUp.oauth;

const InitialSetupDialog = ({
  orgId,
  open,
  onClose
}: {
  orgId: string;
  open: boolean;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <OAuthSetupDialog
      orgId={orgId}
      open={open}
      onClose={onClose}
      name={NAME}
      oauthUrl={'https://signin.swagup.com/oauth2/default/v1/authorize'}
      clientId={OAUTH.clientId}
      redirectUri={OAUTH.redirectUri}
      otherParams={{
        scope: 'offline_access',
        nonce: 1
      }}
    >
      <Alert severity="info" icon={false}>
        {t('org.integrations.swagUp.setupInfo.p1')}
      </Alert>
      <Typography component="p" variant="body1">
        {t('org.integrations.swagUp.setupInfo.p2')}
      </Typography>
    </OAuthSetupDialog>
  );
};

const ExistingSetupDialog = ({
  config,
  open,
  onClose
}: {
  config: Doc<SwagUpIntegrationConfig>;
  open: boolean;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitleWithTypography>
        {NAME} {t('org.integrations.integration')}
      </DialogTitleWithTypography>
      <Content>
        <Alert severity="success">
          {t('org.integrations.swagUp.setupInfo.integrationActive')}
        </Alert>
      </Content>
      <DialogActionsWithSlots
        left={
          <Button color="primary" onClick={onClose}>
            {t('Close')}
          </Button>
        }
        right={
          <ButtonWithPromise
            variant="contained"
            color="secondary"
            onClick={() => removeSwagUpConfig(config.data.orgId)}
            pending={`${t('Removing')}...`}
          >
            {t('Remove')}
          </ButtonWithPromise>
        }
      />
    </Dialog>
  );
};

interface SetupDialogProps
  extends IntegrationSetupDialogWithConfigProperties<SwagUpIntegrationConfig> {
  orgId: string;
}

export const SetupDialog = ({
  config,
  onClose,
  orgId,
  onSuccessfulIntegration, // can be ignored since this integration is not an import source
  open
}: SetupDialogProps) => {
  const setup = !!config?.data.accessToken;
  return (
    <>
      {!setup && (
        <InitialSetupDialog orgId={orgId} open={open} onClose={onClose} />
      )}
      {setup && config && (
        <ExistingSetupDialog config={config} open={open} onClose={onClose} />
      )}
    </>
  );
};

export const SwagUpSettingsButton = ({
  functionalities,
  interactive
}: {
  functionalities: Functionality[];
  interactive: boolean;
}) => {
  const { id: userId, org } = useCurrentUser();
  const [config] = useSwagUpConfig(org.id, userId);
  const cfg = config || null;

  return (
    <IntegrationConfigurationButton
      functionalities={functionalities}
      name={NAME}
      logoUrl="/images/integrations-logos/swagup.svg"
      active={!!cfg?.data.active}
      interactive={interactive}
      renderSetupDialog={({
        open,
        onClose,
        onFailure,
        onSuccessfulIntegration
      }: IntegrationSetupDialogProperties) => (
        <SetupDialog
          orgId={org.id}
          config={cfg}
          open={open}
          onClose={onClose}
          onFailure={onFailure}
          onSuccessfulIntegration={onSuccessfulIntegration}
        />
      )}
    />
  );
};
